import { Grid, Stacked, Typography } from '@statsbomb/kitbag-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMetricDistributionAtoms } from '@/hooks/useMetricDistributionAtoms';
import { RadarDistributionChart } from './RadarDistributionChart';

export type DistributionRowDatum = {
  key: string;
  label: string;
  value: number;
  hasValue?: boolean;
  percentage?: boolean;
  decimalPlaces: number;
  // same scaling as `value`; only set when a comparison is active
  comparisonValue?: number;
  comparisonHasValue?: boolean;
};

export type RadarDistributionListProps = {
  data: DistributionRowDatum[];
  selectedKey?: string;
  onRowClick?: (key: string) => void;
};

export const RadarDistributionList = ({ data, selectedKey, onRowClick }: RadarDistributionListProps) => {
  const { t } = useTranslation('radar');
  const { buckets } = useMetricDistributionAtoms();

  const bucketsByKey = useMemo(() => new Map(buckets.map(bucket => [bucket.metricKey, bucket])), [buckets]);

  return (
    <Stacked direction="column" gap="large">
      {data.map(metric => {
        const distribution = bucketsByKey.get(metric.key);
        const isSelected = metric.key === selectedKey;
        return (
          <button
            key={metric.key}
            type="button"
            aria-pressed={isSelected}
            disabled={!onRowClick}
            onClick={onRowClick ? () => onRowClick(metric.key) : undefined}
          >
            <Grid>
              <Grid item xs={3}>
                <Typography variant="headingSmall" as="span">
                  {metric.label}
                </Typography>
              </Grid>

              <Grid item xs={1} />

              <Grid item xs={8}>
                {distribution?.buckets.length ? (
                  <RadarDistributionChart metric={metric} distribution={distribution} />
                ) : (
                  <Typography variant="bodySmall">{t('distributionView.noDistribution')}</Typography>
                )}
              </Grid>
            </Grid>
          </button>
        );
      })}
    </Stacked>
  );
};
